/**
 * Inbound (two-way) CRM Events feed.
 *
 * Shows the recent inbound events received from downstream CRMs
 * (Builder Prime, HubSpot, etc.) with their processing status.
 *
 * Consumes: GET /api/admin/zapier/inbound-events
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeftRight, RefreshCcw, Loader2, CheckCircle2, XCircle, AlertTriangle,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AUTH = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const STATUS_ICONS = {
  processed: CheckCircle2,
  received: AlertTriangle,
  failed: XCircle,
  duplicate: CheckCircle2,
};

const STATUS_COLORS = {
  processed: 'text-emerald-600',
  received: 'text-amber-600',
  failed: 'text-rose-600',
  duplicate: 'text-slate-500',
};

export default function InboundEventsTab() {
  const [events, setEvents] = useState([]);
  const [crmFilter, setCrmFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = crmFilter
        ? `${API}/admin/zapier/inbound-events?crm_key=${encodeURIComponent(crmFilter)}&limit=50`
        : `${API}/admin/zapier/inbound-events?limit=50`;
      const { data } = await axios.get(url, AUTH());
      setEvents(data.inbound_events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [crmFilter]);

  useEffect(() => { load(); }, [load]);

  const uniqueCrms = Array.from(new Set(events.map((e) => e.crm_key))).sort();

  return (
    <div className="space-y-5" data-testid="inbound-events-tab">
      <Card className="p-4 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <ArrowLeftRight className="w-6 h-6 text-indigo-600" />
          <div>
            <h3 className="text-lg font-semibold">Inbound CRM Events</h3>
            <p className="text-sm text-slate-600 max-w-lg">
              Two-way sync feed — events pushed BACK from your connected CRMs into iEvntür.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={crmFilter}
            onChange={(e) => setCrmFilter(e.target.value)}
            data-testid="inbound-crm-filter"
            className="text-sm border rounded-md px-2 py-1.5"
          >
            <option value="">All CRMs</option>
            {uniqueCrms.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={load} data-testid="inbound-refresh">
            <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </Card>

      {loading && (
        <div className="text-center py-8 text-slate-500" data-testid="inbound-loading">
          <Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading inbound events…
        </div>
      )}

      {!loading && events.length === 0 && (
        <Card className="p-8 text-center text-slate-500" data-testid="inbound-empty">
          <ArrowLeftRight className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          No inbound events yet. Downstream CRMs will POST here via /api/zapier/inbound.
        </Card>
      )}

      <div className="space-y-2">
        {events.map((e) => {
          const Icon = STATUS_ICONS[e.status] || AlertTriangle;
          const color = STATUS_COLORS[e.status] || 'text-slate-500';
          const expanded = expandedId === e.id;
          return (
            <Card
              key={e.id}
              className="p-3 text-sm"
              data-testid={`inbound-event-row-${e.id}`}
            >
              <button
                className="w-full flex items-center gap-3 text-left"
                onClick={() => setExpandedId(expanded ? null : e.id)}
              >
                <Icon className={`w-4 h-4 flex-shrink-0 ${color}`} />
                <div className="flex-1 min-w-0 flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-[10px]">{e.crm_key}</Badge>
                  <span className="font-mono text-xs font-semibold">{e.event_type}</span>
                  <Badge variant="secondary" className="text-[10px]">{e.status}</Badge>
                  {e.remote_id && (
                    <span className="text-xs text-slate-500 truncate">
                      remote #{String(e.remote_id).slice(0, 20)}
                    </span>
                  )}
                  <span className="ml-auto text-xs text-slate-500">
                    {new Date(e.received_at).toLocaleString()}
                  </span>
                </div>
              </button>
              {expanded && (
                <div className="mt-3 pt-3 border-t space-y-2">
                  {e.local_lead_id && (
                    <div className="text-xs">
                      <span className="text-slate-500">Local lead:</span> <code>{e.local_lead_id}</code>
                    </div>
                  )}
                  {e.error && (
                    <div className="text-xs bg-rose-50 text-rose-800 rounded p-2">
                      <strong>Error:</strong> {e.error}
                    </div>
                  )}
                  <div className="text-xs">
                    <div className="text-slate-500 mb-1">Payload:</div>
                    <pre
                      className="bg-slate-50 rounded p-2 overflow-auto text-[11px] max-h-64"
                      data-testid={`inbound-event-payload-${e.id}`}
                    >
                      {JSON.stringify(e.payload, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
